import { kea } from 'kea'
import PropTypes from 'prop-types';
import { filter, keyBy, find } from 'lodash'
import { selector as configSelector } from '~/config'

import {fork, all, spawn} from 'redux-saga/effects'

import * as applications from './applications'
import * as cartItems from './cart-items'
import * as carts from './carts'
import * as checkoutSettings from './checkout-settings'
import * as contacts from './contacts'
import * as downloadAccesses from './download-accesses'
import * as downloads from './downloads'
import * as fileUploads from './file-uploads'
import * as images from './images'
import * as orders from './orders'
import * as orderChartItems from './order-chart-items'
import * as orderHistoryEvents from './order-history-events'
import * as orderItems from './order-items'
import * as orderOverviews from './order-overviews'
import * as orderShippingPrices from './order-shipping-prices'
import * as orderTrackings from './order-trackings'
import * as paymentMethods from './payment-methods'
import * as pricingCategories from './pricing-categories'
import * as pricingCategoryPrices from './pricing-category-prices'
import * as products from './products'
import * as productImages from './product-images'
import * as productImageManagers from './product-image-managers'
import * as productVideos from './product-videos'
import * as productVideoManagers from './product-video-managers'
import * as productQuantities from './product-quantities'
import * as productShippings from './product-shippings'
import * as shippingMethods from './shipping-methods'
import * as shops from './shops'
import * as shopFeeds from './shop-feeds'
import * as shopFeedItems from './shop-feed-items'
import * as shopPaymentMethods from './shop-payment-methods'
import * as shopRoles from './shop-roles'
import * as shopSearches from './shop-searches'
import * as shopShippingMethods from './shop-shipping-methods'
import * as shopVats from './shop-vats'
import * as tickets from './tickets'
import * as ticketPurchases from './ticket-purchases'
import * as ticketPurchaseRegistrations from './ticket-purchase-registrations'
import * as users from './users'
import * as discounts from './discounts'
import * as discountProducts from './discount-products'
import * as orderDiscounts from './order-discounts'
import * as productTags from './product-tags'
import * as productTypes from './product-types'
import * as productVendors from './product-vendors'
import * as collections from './collections'
import * as collectionConditions from './collection-conditions'
import * as collectionProducts from './collection-products'
import * as productAnalytics from './product-analytics'
import * as videos from './videos'
import * as productAccesses from './product-accesses'
import * as productTaxes from './product-taxes'
import * as taxSettings from './tax-settings'
import * as apiKeys from './api-keys'
import * as productMetadata from './product-metadata'
import * as productVariantManagers from './product-variant-managers'
import * as productVariants from './product-variants'

export function * saga() {
  yield all([
    fork(applications.saga),
    fork(cartItems.saga),
    fork(carts.saga),
    fork(checkoutSettings.saga),
    fork(collections.saga),
    fork(collectionConditions.saga),
    fork(collectionProducts.saga),
    fork(contacts.saga),
    fork(downloadAccesses.saga),
    fork(downloads.saga),
    fork(fileUploads.saga),
    fork(images.saga),
    fork(orders.saga),
    fork(orderChartItems.saga),
    fork(orderHistoryEvents.saga),
    fork(orderItems.saga),
    fork(orderOverviews.saga),
    fork(orderShippingPrices.saga),
    fork(paymentMethods.saga),
    fork(pricingCategories.saga),
    fork(pricingCategoryPrices.saga),
    fork(products.saga),
    fork(productAnalytics.saga),
    fork(productImages.saga),
    fork(productImageManagers.saga),
    fork(productVideos.saga),
    fork(productVideoManagers.saga),
    fork(productQuantities.saga),
    fork(productShippings.saga),
    fork(productTags.saga),
    fork(productTypes.saga),
    fork(productVendors.saga),
    fork(shops.saga),
    fork(shopFeeds.saga),
    fork(shopFeedItems.saga),
    fork(shopPaymentMethods.saga),
    fork(shopRoles.saga),
    fork(shopSearches.saga),
    fork(shopShippingMethods.saga),
    fork(shippingMethods.saga),
    fork(shopVats.saga),
    fork(tickets.saga),
    fork(ticketPurchases.saga),
    fork(ticketPurchaseRegistrations.saga),
    fork(users.saga),
    fork(discounts.saga),
    fork(discountProducts.saga),
    fork(orderDiscounts.saga),
    fork(videos.saga),
    fork(productAccesses.saga),
    fork(productTaxes.saga),
    fork(taxSettings.saga),
    fork(apiKeys.saga),
    fork(productMetadata.saga),
    fork(orderTrackings.saga),
    fork(productVariantManagers.saga),
    fork(productVariants.saga),
  ])
}

const filteredEntitySelector = (selectors, entity, key='url') => {
  return [
    () => [
      selectors[entity],
      configSelector,
    ],
    (entities, config) => {
      if(config && config.activeShop) {
        return keyBy(filter(entities, {shop: config.activeShop}), key);
      }
      return entities;
    },
    PropTypes.object
  ]
}

export default kea({
  path : () => ['entities'],

  reducers : ({ actions, constants }) => ({
    applications: [null, PropTypes.object, applications.reducer],
    cartItems: [null, PropTypes.object, cartItems.reducer],
    carts: [null, PropTypes.object, carts.reducer],
    checkoutSettings: [null, PropTypes.object, checkoutSettings.reducer],
    collections: [null, PropTypes.object, collections.reducer],
    collectionConditions: [null, PropTypes.object, collectionConditions.reducer],
    collectionProducts: [null, PropTypes.object, collectionProducts.reducer],
    contacts: [null, PropTypes.object, contacts.reducer],
    discounts: [null, PropTypes.object, discounts.reducer],
    discountProducts: [null, PropTypes.object, discountProducts.reducer],
    downloadAccesses: [null, PropTypes.object, downloadAccesses.reducer],
    downloads: [null, PropTypes.object, downloads.reducer],
    fileUploads: [null, PropTypes.object, fileUploads.reducer],
    images: [null, PropTypes.object, images.reducer],
    orders: [null, PropTypes.object, orders.reducer],
    orderChartItems: [null, PropTypes.object, orderChartItems.reducer],
    orderDiscounts: [null, PropTypes.object, orderDiscounts.reducer],
    orderHistoryEvents: [null, PropTypes.object, orderHistoryEvents.reducer],
    orderItems: [null, PropTypes.object, orderItems.reducer],
    orderOverviews: [null, PropTypes.object, orderOverviews.reducer],
    orderShippingPrices: [null, PropTypes.object, orderOverviews.reducer],
    paymentMethods: [null, PropTypes.object, paymentMethods.reducer],
    pricingCategories: [null, PropTypes.object, pricingCategories.reducer],
    pricingCategoryPrices: [null, PropTypes.object, pricingCategoryPrices.reducer],
    products: [null, PropTypes.object, products.reducer],
    productAnalytics: [null, PropTypes.object, productAnalytics.reducer],
    productImages: [null, PropTypes.object, productImages.reducer],
    productImageManagers: [null, PropTypes.object, productImageManagers.reducer],
    productVideos: [null, PropTypes.object, productVideos.reducer],
    productVideoManagers: [null, PropTypes.object, productVideoManagers.reducer],
    productQuantities: [null, PropTypes.object, productQuantities.reducer],
    productShippings: [null, PropTypes.object, productShippings.reducer],
    productTags: [null, PropTypes.object, productTags.reducer],
    productTypes: [null, PropTypes.object, productTypes.reducer],
    productVendors: [null, PropTypes.object, productVendors.reducer],
    shippingMethods: [null, PropTypes.object, shippingMethods.reducer],
    shops: [null, PropTypes.object, shops.reducer],
    shopFeeds: [null, PropTypes.object, shopFeeds.reducer],
    shopFeedItems: [null, PropTypes.object, shopFeedItems.reducer],
    shopPaymentMethods: [null, PropTypes.object, shopPaymentMethods.reducer],
    shopRoles: [null, PropTypes.object, shopRoles.reducer],
    shopSearches: [null, PropTypes.object, shopSearches.reducer],
    shopShippingMethods: [null, PropTypes.object, shopShippingMethods.reducer],
    shopVats: [null, PropTypes.object, shopVats.reducer],
    tickets: [null, PropTypes.object, tickets.reducer],
    ticketPurchases: [null, PropTypes.object, ticketPurchases.reducer],
    ticketPurchaseRegistrations: [null, PropTypes.object, ticketPurchaseRegistrations.reducer],
    users: [null, PropTypes.object, users.reducer],
    videos: [null, PropTypes.object, videos.reducer],
    productAccesses: [null, PropTypes.object, productAccesses.reducer],
    productTaxes: [null, PropTypes.object, productTaxes.reducer],
    taxSettings: [null, PropTypes.object, taxSettings.reducer],
    apiKeys: [null, PropTypes.object, apiKeys.reducer],
    productMetadata: [null, PropTypes.object, productMetadata.reducer],
    orderTrackings: [null, PropTypes.object, orderTrackings.reducer],
    productVariantManagers: [null, PropTypes.object, productVariantManagers.reducer],
    productVariants: [null, PropTypes.object, productVariants.reducer],
  }),

  selectors : ({ constants, selectors }) => ({
    applications: filteredEntitySelector(selectors, 'applications', 'id'),
    shopPaymentMethods: filteredEntitySelector(selectors, 'shopPaymentMethods'),
    shopShippingMethods: filteredEntitySelector(selectors, 'shopShippingMethods'),
    activeRole: [
      () => [
        selectors.shopRoles,
        configSelector
      ], (shopRoles, config) => {
        const { activeShop } = config;
        if(activeShop) {
          const role = find(shopRoles, {shop: activeShop});

          if(role) {
            return role;
          }
        }

        return null;
      },
      PropTypes.object
    ]
  })
});


